import { useTheme } from '@emotion/react';
import escapeHtml from 'lodash/escape';
import isEqual from 'lodash/isEqual';
import type { FunctionComponent, ReactNode } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Badge } from '~/ui/core/badge/Badge';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { SkillBadge } from '~/ui/domain/skill/SkillBadge';

import { htmlDiffer } from '../../app/common/html-differ';
import { PlainDescriptionSection } from '../../comments/CommentableDescriptionSection';
import type { ChangeProposalCompareTask, EntityIdentifier } from '../../data-contract/chat';
import { useSkillDetails } from '../../skills';
import { ContentSections } from '../components/Common';
import { toProfileTaskRichTextHtml } from '../profile-tasks/profile-task-rich-text';
import {
    getProfileTaskClassificationTranslationKey,
    profileTaskTranslationKeys,
} from '../profile-tasks/profile-task-translations';
import { TaskFieldSection, TaskWorkflowStepRow } from '../profile-tasks/tile/TaskFieldPrimitives';
import { compareTaskContents, isWorkflowStepChanged } from './task-content-comparison';

type Side = 'added' | 'removed';

const Changed: FunctionComponent<{ readonly changed: boolean; readonly children: ReactNode; readonly side: Side }> = ({
    changed,
    children,
    side,
}) => (
    <div
        className={
            changed
                ? side === 'added'
                    ? 'rounded-md bg-status-success-surface'
                    : 'rounded-md bg-status-error-surface'
                : undefined
        }
    >
        {children}
    </div>
);

const TextDiff: FunctionComponent<{ readonly current: string; readonly other: string; readonly side: Side }> = ({
    current,
    other,
    side,
}) => {
    const theme = useTheme();
    const html = htmlDiffer({
        compareAgainst: escapeHtml(side === 'removed' ? current : other),
        current: escapeHtml(side === 'added' ? current : other),
        theme,
        type: side,
    });
    return <PlainDescriptionSection html={html} />;
};

const ResolvedSkill: FunctionComponent<{ readonly skillId: string; readonly title?: string }> = ({
    skillId,
    title,
}) => {
    const { data, isLoading } = useSkillDetails(skillId);
    if (data !== undefined) return <SkillBadge skill={{ title: title ?? data.title, type: data.skillType }} />;
    if (title !== undefined) return <Badge variant="gray">{title}</Badge>;
    return isLoading ? <SkeletonBase className="h-6 w-24" /> : <Badge variant="gray">{skillId}</Badge>;
};

const CompareSkill: FunctionComponent<{
    readonly skill: EntityIdentifier;
    readonly titles: ReadonlyMap<string, string>;
}> = ({ skill, titles }) => {
    const title = skill.title ?? (skill.id ? titles.get(skill.id) : undefined);
    return skill.id ? (
        <ResolvedSkill skillId={skill.id} title={title ?? undefined} />
    ) : title ? (
        <Badge variant="gray">{title}</Badge>
    ) : null;
};

const TaskComparison: FunctionComponent<{
    readonly task: ChangeProposalCompareTask;
    readonly other: ChangeProposalCompareTask | undefined;
    readonly side: Side;
    readonly skillTitles: ReadonlyMap<string, string>;
}> = ({ task, other, side, skillTitles }) => {
    const translate = useTranslate();
    const steps = task.workflow?.steps ?? [];
    const skills = task.requiredSkills ?? [];
    return (
        <article className="flex min-w-0 flex-col gap-xl" data-testid="task-content-comparison">
            <div className="text-body-s font-medium">
                <TextDiff current={task.title} other={other?.title ?? ''} side={side} />
            </div>
            {task.description ? (
                <Changed changed={task.description !== other?.description} side={side}>
                    <PlainDescriptionSection html={toProfileTaskRichTextHtml(task.description)} />
                </Changed>
            ) : null}
            {steps.length > 0 ? (
                <TaskFieldSection title={translate(profileTaskTranslationKeys.workflow)}>
                    <ol className="m-0 flex list-none flex-col gap-s p-0" data-testid="task-comparison-workflow">
                        {steps.map((step, index) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: workflow snapshots have no step identity and rows are read-only
                            <li key={`${index}-${step.title}`} className="list-none">
                                <Changed changed={isWorkflowStepChanged(task, other, index)} side={side}>
                                    <TaskWorkflowStepRow
                                        className="flex gap-m rounded-xl bg-transparent px-xl py-xl"
                                        description={step.description ?? undefined}
                                        index={index}
                                        meta={
                                            step.physicality ? (
                                                <Badge data-testid="task-comparison-physicality" variant="gray">
                                                    {step.physicality === 'physical'
                                                        ? translate(
                                                              'pages.job-architecture-v2.comparision.physical-step',
                                                          )
                                                        : step.physicality === 'non-physical'
                                                          ? translate(
                                                                'pages.job-architecture-v2.comparision.non-physical-step',
                                                            )
                                                          : step.physicality}
                                                </Badge>
                                            ) : undefined
                                        }
                                        title={step.title}
                                    />
                                </Changed>
                            </li>
                        ))}
                    </ol>
                </TaskFieldSection>
            ) : null}
            {task.classification ? (
                <TaskFieldSection title={translate(profileTaskTranslationKeys.classification)}>
                    <Changed changed={!isEqual(task.classification, other?.classification)} side={side}>
                        <Badge variant={task.classification.value === 'contribution' ? 'success' : 'gray'}>
                            {translate(getProfileTaskClassificationTranslationKey(task.classification.value))}
                        </Badge>
                    </Changed>
                </TaskFieldSection>
            ) : null}
            {task.estimatedEffort ? (
                <TaskFieldSection title={translate(profileTaskTranslationKeys.effort)}>
                    <Changed changed={!isEqual(task.estimatedEffort, other?.estimatedEffort)} side={side}>
                        <Badge variant="gray">
                            {translate(profileTaskTranslationKeys.effortFormat, {
                                minimum: task.estimatedEffort.minimumHoursPerWeek,
                                maximum: task.estimatedEffort.maximumHoursPerWeek,
                            })}
                        </Badge>
                    </Changed>
                </TaskFieldSection>
            ) : null}
            {skills.length > 0 ? (
                <TaskFieldSection title={translate(profileTaskTranslationKeys.skills)}>
                    <ul className="m-0 flex list-none flex-wrap gap-m p-0">
                        {skills.map((skill, index) => (
                            <li key={skill.id ?? skill.title ?? index} className="flex min-w-0 items-center gap-s">
                                <CompareSkill skill={skill} titles={skillTitles} />
                                {!(other?.requiredSkills ?? []).some((candidate) =>
                                    candidate.id ? candidate.id === skill.id : candidate.title === skill.title,
                                ) ? (
                                    <Badge className="shrink-0" variant={side === 'added' ? 'success' : 'error'}>
                                        {translate(`components.skills.${side}`)}
                                    </Badge>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </TaskFieldSection>
            ) : null}
        </article>
    );
};

export const TaskContentsComparisonSection: FunctionComponent<{
    readonly left: readonly ChangeProposalCompareTask[];
    readonly right: readonly ChangeProposalCompareTask[];
    readonly skillTitles: ReadonlyMap<string, string>;
}> = ({ left, right, skillTitles }) => {
    const translate = useTranslate();
    const sides = compareTaskContents(left, right);
    return (
        <ContentSections columns="two">
            {sides.map(({ side, tasks }) => (
                <section
                    key={side}
                    className="min-w-0"
                    data-testid={side === 'removed' ? 'tasks-diff-left' : 'tasks-diff-right'}
                >
                    <TaskFieldSection title={translate('pages.job-architecture-v2.comparision.tasks-section')}>
                        <div className="flex flex-col gap-group">
                            {tasks.map(({ task, other }, index) => (
                                <TaskComparison
                                    key={task.id ?? task.key ?? index}
                                    other={other}
                                    side={side}
                                    skillTitles={skillTitles}
                                    task={task}
                                />
                            ))}
                        </div>
                    </TaskFieldSection>
                </section>
            ))}
        </ContentSections>
    );
};
